import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button, message, Modal, Space, Steps
} from 'antd';
import { TFunction } from 'i18next';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import HeaderPage from 'common/components/HeaderPage';
import getDropdownTypeService from 'common/services/dropdownType';
import {
  getAllAddOnsPackageService,
  getPackageDetailService,
  postCreatePackageService,
  putUpdatePackageService
} from 'common/services/packages';
import { PackageDetailTypes } from 'common/services/packages/types';
import { KNOWLEDGE_SOURCES, ROUTE_PATHS } from 'common/utils/constant';
import { packageDetailSchema } from 'common/utils/schemas';
import PackageGeneralInfo from 'extends/PackageDetail/components/PackageGeneralInfo';
import PackageTierConfig from 'extends/PackageDetail/components/PackageTierConfig';

export type FormTierValues = {
  index: number;
  name: string;
  numberOfAgent: number;
  knowledges: boolean[];
  knowledgeLimits: {
    numberOfFile: number;
    fileSizeLimit: number;
    totalSizeLimit: number;
    numberOfWeb: number;
  };
  customServices: {
    [key: string]: boolean;
  };
  endUserSides: {
    numberOfSession: number;
    chatUpload: boolean;
    chatExport: boolean;
    agentTypeBoth: boolean;
    s2t: boolean;
    t2s: boolean;
  },
  price: number;
};

export type AddOnOptions = {
  value: number;
  label: string;
};

export type FormValues = {
  name: string;
  basePrice: number;
  duration: { value: number; label: string };
  isApplyPayg: boolean;
  tiers: FormTierValues[];
  servicePackageAddOns?: number[]
};

export const packageDurations = (t: TFunction) => [
  {
    label: t('package.aWeek'),
    value: 1,
  },
  {
    label: t('package.aMonth'),
    value: 2,
  },
  {
    label: t('package.aYear'),
    value: 3,
  },
  {
    label: t('package.unlimited'),
    value: 4,
  },
];

const PackageDetail = () => {
  /* Hook */
  const { t } = useTranslation();
  const method = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      basePrice: 0,
      isApplyPayg: false,
      tiers: [],
      servicePackageAddOns: []
    },
    resolver: yupResolver(packageDetailSchema),
  });

  /* Selectors */
  const navigate = useNavigate();

  /* Search Params */
  const [searchParams] = useSearchParams();
  const idParams = Number(searchParams.get('id'));

  /* States */
  const [step, setStep] = useState(0);
  const [confirm, setConfirm] = useState<boolean>(false);

  /* React-query */
  const { isLoading: isPackageDetailLoading } = useQuery(
    ['getPackageDetail'],
    () => getPackageDetailService(idParams),
    {
      onSuccess: (data) => {
        const formattedTiers = data.servicePackageData.tiers?.map((tier) => ({
          ...tier,
          knowledges:
            Array.from(
              { length: Object.entries(KNOWLEDGE_SOURCES).length + 1 },
              (_, i) => (tier.knowledges?.includes(i) ? true : undefined)
            ),
          customServices: Object.fromEntries(
            (tier.customServices || []).map((key) => [key, true])
          )
        }));

        method.reset({
          name: data.servicePackageData.name,
          duration: {
            label: packageDurations(t).find(
              (item) => item.value === data.servicePackageData.duration
            )?.label,
            value: data.servicePackageData.duration
          },
          basePrice: data.servicePackageData.basePrice,
          isApplyPayg: data.servicePackageData.isApplyPayg,
          tiers: formattedTiers,
          servicePackageAddOns: data.servicePackageData.servicePackageAddOnData?.map(
            (item) => item.id
          )
        });
      },
      enabled: !!searchParams.get('id'),
    }
  );

  /* React-query */
  const { data: addOnsPackage, isLoading: isAddOnsPackageLoading } = useQuery(
    ['packageData', method.watch().duration],
    () => getAllAddOnsPackageService({ duration: method.watch().duration?.value }),
  );

  const { data: customServices, isLoading: isCustomServiceLoading } = useQuery(
    ['getAllCustomServices'],
    () => getDropdownTypeService<any>('ai-assistant-service', {
      page: 1,
      limit: 200,
    }),
  );

  const { mutate: createPackageMutate } = useMutation(
    ['createPackage'],
    (params: PackageDetailTypes) => postCreatePackageService(params),
    {
      onSuccess: () => {
        message.success(t('package.createSuccess'));
        navigate(ROUTE_PATHS.PACKAGES_MANAGEMENT);
      },
      onError: (errors: ErrorResponse[]) => {
        if (errors.length > 0) {
          errors.forEach((error: ErrorResponse) => {
            method.setError(error.field as keyof FormValues, { message: error.message || '' });
          });
          message.error(t('package.createError'));
        } else {
          message.error(t('package.createError'));
        }
      },
    }
  );

  const { mutate: updatePackageMutate } = useMutation(
    ['updatePackage', idParams],
    (params: PackageDetailTypes) => putUpdatePackageService(idParams, params),
    {
      onSuccess: () => {
        message.success(t('package.updateSuccess'));
        navigate(ROUTE_PATHS.PACKAGES_MANAGEMENT);
      },
      onError: (errors: ErrorResponse[]) => {
        if (errors.length > 0) {
          errors.forEach((error: ErrorResponse) => {
            method.setError(error.field as keyof FormValues, { message: error.message || '' });
          });
        } else {
          message.error(t('package.updateError'));
        }
      },
    }
  );

  /* Functions */
  const handleChangeStep = async (currentStep: number) => {
    const isValid = await method.trigger(['name', 'duration']);
    if (!isValid) return;

    setStep(currentStep);
  };

  const handleSubmit = () => {
    // Formatted data to send to API
    const formattedData = {
      ...method.getValues(),
      duration: method.getValues().duration.value,
      tiers: method.getValues().tiers?.map((tier, idx) => ({
        ...tier,
        index: idx + 1,
        knowledges:
          (tier.knowledges)?.reduce((acc: number[], val: boolean | undefined, index: number) => {
            if (val === true) acc.push(index);
            return acc;
          }, []),
        customServices: Object.entries(tier.customServices)
          ?.filter(([, value]) => value === true)
          .map(([key]) => key)
      })),
    };

    if (idParams) {
      updatePackageMutate(formattedData);
    } else {
      createPackageMutate(formattedData);
    }
  };

  /* Memos */
  const createPackageSteps = [
    {
      title: t('package.generalInfo'),
      content: <PackageGeneralInfo
        method={method}
        addOnsPackageData={addOnsPackage?.data.map((item) => (
          { id: item.servicePackageData.id, name: item.servicePackageData.name }))}
        isLoading={isPackageDetailLoading || isAddOnsPackageLoading || isCustomServiceLoading}
      />,
    },
    {
      title: t('package.tierConfig'),
      content: <PackageTierConfig
        method={method}
        customServicesData={customServices?.data}
        isLoading={isPackageDetailLoading || isCustomServiceLoading}
      />,
    },
  ];

  const totalSteps = createPackageSteps.length - 1;

  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.packageDetail')}
      />
      <div className="t-mainlayout_wrapper">
        <FormProvider {...method}>
          <Steps current={step} items={createPackageSteps} />
          <div>{createPackageSteps[step].content}</div>
          <Space direction="horizontal" align="end" className="u-mt-16 p-packageDetail_actions">
            <Button disabled={step === 0} onClick={() => handleChangeStep(step - 1)}>{t('package.back')}</Button>
            {step === totalSteps ? (
              <>
                <Button onClick={() => { setConfirm(true); }}>{step === totalSteps && t('package.save')}</Button>
                <Modal
                  title={t('package.saveChanges')}
                  open={confirm}
                  onOk={() => {
                    handleSubmit();
                    setConfirm(false);
                  }}
                  okText={t('package.save')}
                  cancelText={t('system.cancel')}
                  onCancel={() => { setConfirm(false); }}
                >
                  {t('package.confirmSave')}
                </Modal>
              </>
            ) : (
              <Button onClick={() => handleChangeStep(step + 1)}>{t('package.next')}</Button>
            )}
          </Space>
        </FormProvider>
      </div>
    </>
  );
};

export default PackageDetail;
